import axios from "axios";
import {
  getL2ExplorerUrl,
  getChainName,
  getRpcUrls,
  getRpcWsUrls,
} from "../../config/chains";

// Now uses centralized config based on REACT_APP_CHAIN_ID
export const BLOCK_EXPLORER_URL = getL2ExplorerUrl();
export const CHAIN_NAME = getChainName();
export const RPC_URLS = getRpcUrls();
export const RPC_WS_URLS = getRpcWsUrls();
export const PUBLIC_MONAD_RPC_URL = "https://testnet-rpc.monad.xyz";
export const BLOCK_RPC_URLS = [
  ...RPC_URLS.filter((url) => url === PUBLIC_MONAD_RPC_URL),
  ...RPC_URLS.filter((url) => url !== PUBLIC_MONAD_RPC_URL),
];
/**
 * Explorers whose block API a BROWSER cannot call, so we do not try.
 *
 * arcscan sends no `Access-Control-Allow-Origin` header at all, so the request
 * is blocked before it is answered -- on every origin, in every environment,
 * including production. Attempting it can only ever produce a console error and
 * a rejected promise; it cannot ever produce a block number.
 *
 * It surfaced as two failing `console-health` specs. That spec seals hosts
 * matching `realtime.exchange`, which includes Arc's RPC, so the RPC returned
 * `{}` and the footer fell through to this fallback -- which is exactly the
 * path a real RPC outage would take. The test made a production-possible call
 * visible; it did not invent it.
 */
export const SKIP_EXPLORER_BLOCK_API =
  BLOCK_EXPLORER_URL.includes("monadexplorer.com") ||
  BLOCK_EXPLORER_URL.includes("monadvision.com") ||
  BLOCK_EXPLORER_URL.includes("bscscan.com") ||
  BLOCK_EXPLORER_URL.includes("arcscan.app");

export const MAINTENANCE_TIMESTAMP =
  process.env.REACT_APP_MAINTENANCE_TIMESTAMP_SECONDS;

export function parseRpcBlockNumber(result?: unknown): number | undefined {
  if (typeof result !== "string" || !/^0x[0-9a-f]+$/i.test(result)) {
    return undefined;
  }
  const blockNumber = parseInt(result, 16);
  return Number.isFinite(blockNumber) && blockNumber > 0
    ? blockNumber
    : undefined;
}

export function parseWebSocketBlockNumber(
  messageData: string
): number | undefined {
  let message: any;
  try {
    message = JSON.parse(messageData);
  } catch {
    return undefined;
  }

  return parseRpcBlockNumber(
    message?.params?.result?.number ?? message?.result?.number
  );
}

export async function fetchExplorerBlockNumber(): Promise<number | undefined> {
  if (SKIP_EXPLORER_BLOCK_API) return undefined;
  const { data } = await axios.get(
    `${BLOCK_EXPLORER_URL}/api?module=block&action=eth_block_number`
  );
  return parseRpcBlockNumber(data?.result);
}

export async function fetchRpcBlockNumber(): Promise<number | undefined> {
  for (const rpcUrl of BLOCK_RPC_URLS) {
    if (!rpcUrl) continue;
    try {
      const { data } = await axios.post(
        rpcUrl,
        {
          jsonrpc: "2.0",
          id: 1,
          method: "eth_blockNumber",
          params: [],
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 10_000,
        }
      );
      const blockNumber = parseRpcBlockNumber(data?.result);
      if (blockNumber) return blockNumber;
    } catch {
      // Try the next configured RPC URL.
    }
  }
  return undefined;
}
